import { useLingui } from "@lingui/react/macro"
import type { SearchResponse, SearchTab } from "@/types/v2/search"
import { SEARCH_TABS, SEARCH_TAB_LABELS } from "@/types/v2/search"
import { useScrollFade, scrollFadeMask } from "@/hooks/use-scroll-fade"

// Type-filter strip for the command palette (CON-183).
//
// Active tab = dark filled pill (`#1E1B17` bg, cream text);
// the rest are plain muted text. Each non-`All` tab shows its
// result count when > 0. The strip scrolls horizontally with
// an edge fade (CLAUDE.md "Horizontal chip/tab strips scroll
// with an edge fade") so it never wraps or overflows on
// narrow mobile widths.

interface SearchTabsProps {
  active: SearchTab
  onSelect: (tab: SearchTab) => void
  // Per-tab counts from the latest response; null in the
  // cold-start state (no query run yet) so no badges show.
  counts: SearchResponse["counts"] | null
}

export function SearchTabs({ active, onSelect, counts }: SearchTabsProps) {
  const { t, i18n } = useLingui()
  const { ref, fadeStart, fadeEnd } = useScrollFade()
  const mask = scrollFadeMask({ fadeStart, fadeEnd })

  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={t`Filter results by type`}
      style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
      className="flex min-w-0 items-center gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {SEARCH_TABS.map((tab) => {
        const isActive = tab === active
        const count = counts ? counts[tab] : 0
        const showCount = tab !== "all" && count > 0
        return (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelect(tab)}
            data-testid={`search-tab-${tab}`}
            className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] leading-4 ${
              isActive
                ? "bg-[#1E1B17] font-medium text-[#F0EDE6]"
                : "text-[#7A746B] hover:bg-[#F0EDE6]"
            }`}
          >
            <span>{i18n._(SEARCH_TAB_LABELS[tab])}</span>
            {showCount ? (
              <span
                data-testid={`search-tab-count-${tab}`}
                className={`text-[11px] leading-3.5 ${
                  isActive ? "text-[#C9C2B6]" : "text-[#9A938A]"
                }`}
              >
                {count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
